import { TiktokUser, creatorbase, TiktokUserImage, TiktokUserStatsCache } from '@round/api';
import { isReceivedCampaign } from 'Modules/Plans/Campaigns/helpers';
import { Skeleton, TableProps, getTableMetaHelper } from '@round/ui-kit';
import { Button, lucideIcons } from '@round/creatorbase-ui';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import styles from '../PostsTable.module.css';
import CostCell from 'Modules/Plans/components/CostCell/CostCell';
import { CampaignTableRow } from '../../ProjectCampaignsTable/ProjectCampaignsTable';
import TotalCostFooterCell from 'Modules/Plans/components/TotalCostFooterCell/TotalCostFooterCell';
import ActionsCell from '../components/ActionsCell/ActionsCell';
import NotesCell from 'Modules/Plans/components/NotesCell/NotesCell';
import { getTiktokEngagementRate, getTikTokTotalEngagement } from 'Modules/Plans/Posts/helpers';
import PostsTable from 'Modules/Plans/Posts/components/PostsTable/PostsTable';
import { BasePostTableMeta } from 'Modules/Plans/Posts/types';
import { useAggregatedPostStats } from '@round/creatorbase-helpers/api-hooks';
import UpdateInfluencerPopout from '../components/UpdateInfluencerPopout/UpdateInfluencerPopout';
import { CopyButton } from '@round/creatorbase-helpers/components';
import cn from 'classnames';
import PostsTableEngagementsFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableEngagementsFooterCell';
import PostsTableEngagementRateFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableEngagementRateFooterCell';
import PostStatsViewsFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostStatsViewsFooterCell';
import PostsTableUploadedDateCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableUploadedDateCell';
import PostsTableStatusCell, {
    StatusData,
} from 'Modules/Plans/Posts/components/PostsTable/cells/StatusCell/PostsTableStatusCell';
import UserBadge, { UserBadgeProps } from 'Modules/Plans/User/UserBadge/UserBadge';
import TiktokUserModal from 'Modules/Plans/User/UserModal/TiktokUserModal/TiktokUserModal';
import PostStatsWithAverageCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsStatsWithAverageCell/PostsStatsWithAverageCell';
import PostStatsWithAverageFooterTotalCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsStatsWithAverageCell/PostStatsWithAverageFooterCell';
import ManualPaymentStatusCell from '../components/ManualPaymentStatusCell/ManualPaymentStatusCell';
import EstimatedViewsCell from '../components/EstimatedViewsCell/EstimatedViewsCell';
import ContactDetailsCell from '../components/ContactDetailsCell/ContactDetailsCell';
import { asMoneyWithoutZeros, showNotification } from 'helpers';
import CreatorDetailsModal from 'Modules/Plans/CreatorSearch/components/CreatorDetailsModal/CreatorDetailsModal';
import UpdateCostCell from '../components/UpdateCostCell/UpdateCostCell';
import PaymentStatusCell from '../components/PaymentStatusCell/PaymentStatusCell';
import PostsTableStatFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableStatFooterCell';
import { cpm } from 'Modules/Advertising/Metrics/Metrics';
import { TooltipHeader } from '@round/whitelabel-ui';
import { tooltipCopy } from 'Modules/Plans/helpers';
import { buildTiktokUserUrl } from 'helpers';
import { ModalConfig } from '../../../../../CreatorSearch/components/CreatorDetailsModal/helpers';

const { ChartSpline: DiagramIcon } = lucideIcons;

export type TiktokPostsTableRow = creatorbase.TiktokPost & {
    user?: TiktokUser | null;
    userStatsCache?: TiktokUserStatsCache | null;
    image?: TiktokUserImage | null;
    payee?: creatorbase.ClientPayee | null;
};

type Props = Pick<TableProps<TiktokPostsTableRow>, 'data' | 'noDataLabel'> & {
    campaign: CampaignTableRow;
    isLoading?: boolean;
    isAccountDataLoading?: boolean;
    hasError: boolean;
    onCreatePost?: (campaign: CampaignTableRow) => void;
    onAddCreatorsToCampaign?: (campaign: CampaignTableRow) => void;
    onDeletePost: (post: TiktokPostsTableRow) => void;
    onOpenPostStatsModal?: (post: TiktokPostsTableRow) => void;
    updatePost: typeof creatorbase.patchPost;
    onPostStatusUpdated?: (status: creatorbase.PostManualStatus) => void;
    isPaymentEnabled?: boolean;
};

type TableCellContext<TKey extends keyof TiktokPostsTableRow> = CellContext<
    TiktokPostsTableRow,
    TiktokPostsTableRow[TKey]
>;

type Meta = BasePostTableMeta<TiktokPostsTableRow> & {
    campaign: CampaignTableRow;
    onDeletePost: (post: TiktokPostsTableRow) => void;
};
const getTableMeta = getTableMetaHelper<Meta>();

const TiktokPostsTable = ({
    data,
    campaign,
    isLoading,
    isAccountDataLoading,
    hasError,
    updatePost,
    onCreatePost,
    onAddCreatorsToCampaign,
    onDeletePost,
    onOpenPostStatsModal,
    isPaymentEnabled,
    onPostStatusUpdated,
}: Props) => {
    const { isFetching: areCampaignStatsLoading } = useAggregatedPostStats({ campaign_id: campaign.id });
    const [accountModalUserId, setAccountModalUserId] = useState<TiktokUser['id'] | null>(null);

    const [clientAccountModalConfig, setClientAccountModalConfig] = useState<ModalConfig | null>(null);

    const columns = useMemo<ColumnDef<TiktokPostsTableRow, any>[]>(
        () => [
            {
                header: 'ID',
                accessorKey: 'id',
                cell: ({ getValue, table }: TableCellContext<'id'>) => {
                    const { isLoading } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }

                    return getValue().slice(-8);
                },
            },
            {
                header: 'Account',
                id: 'account',
                cell: ({ row: { original }, table }) => {
                    const { isLoading, getIsAccountDataLoading, updatePost, managedBy, isReceived } =
                        getTableMeta(table);
                    const isManagedExternally = managedBy !== 'creatorbase' || !!isReceived;
                    const image =
                        original?.image?.avatar_thumb?.cached_url || original?.image?.avatar_thumb?.original_url || '';
                    const followerCount = original?.userStatsCache?.follower_count || null;
                    const accountHandle = original.user?.unique_id ? `@${original.user?.unique_id}` : '-';

                    const userBadgeProps: UserBadgeProps = {
                        isLoading: isLoading || getIsAccountDataLoading(original),
                        image: image,
                        title: accountHandle,
                        followerCount: followerCount,
                        profileUrl: original.user?.unique_id ? buildTiktokUserUrl(original.user.unique_id) : null,
                    };
                    const handleOpenAccountModal = async () => {
                        if (!original.tiktok_details?.account_id) {
                            showNotification("Couldn't open the user modal", 'error');
                            return;
                        }

                        if (!original.client_influencer_account_id) {
                            showNotification(
                                'No related influencer account found. Opening the demographics modal',
                                'info'
                            );
                            setAccountModalUserId(original.tiktok_details.account_id);
                            return;
                        }

                        setClientAccountModalConfig({
                            clientInfluencerAccountId: original.client_influencer_account_id,
                            platformUserId: original.tiktok_details.account_id,
                        });
                    };

                    return (
                        <div className={styles.accountContainer}>
                            <UserBadge {...userBadgeProps} />

                            <div className={cn(styles.actionsContainer, 'twp')}>
                                {original.user?.unique_id && (
                                    <CopyButton
                                        value={original.user.unique_id}
                                        tooltipLabel="Copy account handle"
                                        variant="ghost"
                                        size="icon-sm"
                                        className={cn(styles.iconButton, styles.hoverActionButton)}
                                    />
                                )}
                                {!original.post_url && !isManagedExternally && (
                                    <UpdateInfluencerPopout
                                        key={accountHandle}
                                        className={cn(styles.iconButton, styles.hoverActionButton)}
                                        accountHandle={accountHandle ?? ''}
                                        updateAccountHandle={
                                            updatePost
                                                ? (accountHandle) =>
                                                      updatePost?.(original.id, {
                                                          influencer_account_identifier: accountHandle,
                                                      })
                                                : undefined
                                        }
                                    />
                                )}
                                {/* We don't expect client influencer accounts or phyllo data for microwave influencers */}
                                {managedBy !== 'microwave' && (
                                    <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        className={styles.iconButton}
                                        onClick={handleOpenAccountModal}
                                    >
                                        <DiagramIcon />
                                    </Button>
                                )}
                            </div>
                        </div>
                    );
                },
            },
            {
                header: 'Contact details',
                id: 'contactDetails',
                accessorKey: 'contact_details',
                cell: ContactDetailsCell,
            },
            {
                header: 'Status',
                id: 'status',
                accessorFn: (original): StatusData => ({
                    postUrl: original.post_url,
                    platform: original.platform,
                    adCode: original.tiktok_details?.ad_code,
                }),
                cell: PostsTableStatusCell,
            },
            {
                header: 'Cost',
                accessorKey: 'cost',
                cell: ({ getValue, row: { original }, table }: TableCellContext<'cost'>) => {
                    const { isLoading, updatePost, campaign } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }

                    return (
                        <CostCell
                            cost={getValue()}
                            currency={campaign.currency ?? null}
                            updateCost={
                                updatePost ? (cost: number | null) => updatePost(original.id, { cost }) : undefined
                            }
                            isReadonly={!campaign.is_post_cost_editable}
                        />
                    );
                },
                footer: ({ table }) => {
                    const { isLoading, campaign } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }

                    return (
                        <TotalCostFooterCell
                            costs={table.getRowModel().rows.map((r) => r.original.cost)}
                            currency={campaign.currency}
                        />
                    );
                },
            },
            {
                header: 'Cost',
                accessorKey: 'cost',
                id: 'costWithFees',
                cell: UpdateCostCell,
                footer: ({ table }) => {
                    const { isLoading, campaign } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }

                    return (
                        <TotalCostFooterCell
                            costs={table.getRowModel().rows.map((r) => r.original.cost)}
                            currency={campaign.currency}
                        />
                    );
                },
            },
            {
                header: 'Payment Status',
                id: 'manualPaymentStatus',
                accessorKey: 'payment_status',
                cell: ManualPaymentStatusCell,
            },
            {
                header: 'Payment Status',
                id: 'paymentStatus',
                accessorKey: 'payment_request_status',
                cell: PaymentStatusCell,
            },
            {
                header: 'Uploaded date',
                id: 'uploadedDate',
                cell: PostsTableUploadedDateCell,
            },
            {
                header: 'Est. views',
                meta: {
                    className: styles.estimatedViewsCell,
                },
                id: 'estimatedViews',
                accessorFn: (row) => row.estimated_views ?? row.userStatsCache?.avg_post_views,
                cell: (props) => (
                    <EstimatedViewsCell
                        {...props}
                        //The key is important here to ensure the cell re-renders when the user stats loads
                        key={props.row.original.userStatsCache?.avg_post_views}
                    />
                ),
                footer: PostsTableStatFooterCell,
            },
            {
                header: 'Est. CPM',
                id: 'estimatedCPM',
                accessorFn: (row) => {
                    const views = row.estimated_views ?? row.userStatsCache?.avg_post_views;
                    const estimatedCpm = cpm(row.cost, views);
                    return isFinite(estimatedCpm) ? estimatedCpm : '-';
                },
                cell: ({ table, getValue }) => {
                    const { isLoading, campaign } = getTableMeta(table);
                    if (isLoading) {
                        return <Skeleton />;
                    }

                    const cpm = getValue();
                    return typeof cpm === 'number' ? asMoneyWithoutZeros(cpm, campaign.currency) : '-';
                },
                footer: ({ table }) => {
                    const { isLoading, campaign } = getTableMeta(table);
                    if (isLoading) {
                        return <Skeleton />;
                    }

                    const rows = table.getRowModel().rows;
                    const estimatedViews = rows.reduce((acc, { original: row }) => {
                        const estimatedViews = row.estimated_views ?? row.userStatsCache?.avg_post_views ?? 0;
                        return estimatedViews + acc;
                    }, 0);

                    const cost = rows.reduce((acc, { original: row }) => {
                        return acc + (row.cost ?? 0);
                    }, 0);

                    const estimatedCpm = cpm(cost, estimatedViews);
                    return isFinite(estimatedCpm) ? asMoneyWithoutZeros(estimatedCpm, campaign.currency) : '-';
                },
            },
            {
                header: 'Views',
                id: 'views',
                accessorFn: (row) => ({
                    value: row.view_count,
                    average: row.userStatsCache?.avg_post_views,
                }),
                cell: PostStatsWithAverageCell,
                footer: PostStatsViewsFooterCell,
            },
            {
                header: 'Total engagements',
                id: 'totalEngagements',
                accessorFn: (row) => ({
                    value: getTikTokTotalEngagement(row),
                    average: row.userStatsCache?.avg_post_total_engagements,
                }),
                cell: PostStatsWithAverageCell,
                footer: PostsTableEngagementsFooterCell,
            },
            {
                header: () => <TooltipHeader header="ER" tooltipBody={tooltipCopy.engagementRate} />,
                id: 'engagementRate',
                accessorFn: (row) => ({
                    value: getTiktokEngagementRate(row),
                    average: row.userStatsCache?.avg_engagement_rate,
                    formatValueToPercentage: true,
                }),
                cell: PostStatsWithAverageCell,
                footer: PostsTableEngagementRateFooterCell,
            },
            {
                header: 'Likes',
                id: 'likes',
                accessorFn: (row) => ({
                    value: row.tiktok_details?.like_count,
                    average: row.userStatsCache?.avg_post_likes,
                }),
                cell: PostStatsWithAverageCell,
                footer: PostStatsWithAverageFooterTotalCell,
            },
            {
                header: 'Shares',
                id: 'shares',
                accessorFn: (row) => ({
                    value: row.tiktok_details?.share_count,
                    average: row.userStatsCache?.avg_post_shares,
                }),
                cell: PostStatsWithAverageCell,
                footer: PostStatsWithAverageFooterTotalCell,
            },
            {
                header: 'Comments',
                id: 'comments',
                accessorFn: (row) => ({
                    value: row.tiktok_details?.comment_count,
                    average: row.userStatsCache?.avg_post_comments,
                }),
                cell: PostStatsWithAverageCell,
                footer: PostStatsWithAverageFooterTotalCell,
            },
            {
                header: 'Saves',
                id: 'saves',
                accessorFn: (row) => ({
                    value: row.tiktok_details?.save_count,
                    average: row.userStatsCache?.avg_post_saves,
                }),
                cell: PostStatsWithAverageCell,
                footer: PostStatsWithAverageFooterTotalCell,
            },
            {
                header: 'Notes',
                accessorKey: 'notes',
                cell: ({ getValue, row: { original }, table }: TableCellContext<'notes'>) => {
                    const { isLoading, updatePost } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }
                    return (
                        <NotesCell
                            notes={getValue()}
                            updateNotes={updatePost ? (notes: string) => updatePost(original.id, { notes }) : undefined}
                        />
                    );
                },
            },
            {
                header: '',
                id: 'actions',
                cell: ({ row: { original }, table }) => {
                    const { isLoading, onDeletePost } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }

                    return <ActionsCell onDelete={() => onDeletePost(original)} />;
                },
            },
        ],
        []
    );

    // meta object is passed to every cell component. This allows us to reduce rerenders and prevents unnecessary cell unmounts.
    const isReceived = isReceivedCampaign(campaign);

    const meta: Meta = {
        isLoading: !!isLoading,
        getIsAccountDataLoading: (row) => (row.tiktok_details?.account_id ? !!isAccountDataLoading : false),
        areCampaignStatsLoading,
        onDeletePost,
        onOpenPostStatsModal,
        onCreatePost: onCreatePost ? () => onCreatePost(campaign) : undefined,
        onAddCreators: onAddCreatorsToCampaign ? () => onAddCreatorsToCampaign(campaign) : undefined,
        updatePost,
        campaign,
        managedBy: campaign.managed_by,
        isReceived,
        campaignStats: campaign.stats,
        onPostStatusUpdated,
    };

    const shouldShowIntegratedPaymentColumns = Boolean(
        !isReceived && campaign.is_post_cost_editable && campaign.managed_by === 'creatorbase' && isPaymentEnabled
    );

    const shouldShowManualPaymentColumns = Boolean(
        !isReceived && campaign.is_post_cost_editable && campaign.managed_by === 'creatorbase' && !isPaymentEnabled
    );

    return (
        <>
            <PostsTable
                id={`post-table-${campaign.id}`}
                className={styles.table}
                columns={columns}
                data={data}
                meta={meta}
                isLoading={isLoading}
                hasError={hasError}
                columnVisibility={{
                    contactDetails: campaign.managed_by === 'creatorbase' && !isReceived,
                    costWithFees: shouldShowIntegratedPaymentColumns,
                    paymentStatus: shouldShowIntegratedPaymentColumns,
                    cost: shouldShowManualPaymentColumns,
                    manualPaymentStatus: shouldShowManualPaymentColumns,
                    estimatedCPM: !isReceived && campaign.is_post_cost_editable,
                    actions: campaign.managed_by === 'creatorbase' && !isReceived,
                    notes: !isReceived,
                }}
            />
            <TiktokUserModal
                key={accountModalUserId}
                isOpen={typeof accountModalUserId === 'number'}
                onClose={() => {
                    setAccountModalUserId(null);
                }}
                userId={accountModalUserId}
            />

            <CreatorDetailsModal
                key={clientAccountModalConfig?.clientInfluencerAccountId}
                isOpen={!!clientAccountModalConfig}
                onClose={() => setClientAccountModalConfig(null)}
                clientInfluencerAccountId={clientAccountModalConfig?.clientInfluencerAccountId}
            />
        </>
    );
};

export default TiktokPostsTable;
